import { HelpTip } from './types';

export const SETTINGS_TIPS = {
  /* === Check points === */
  checkPointsTable: {
    title: { fr: 'Les points d’inspection', en: 'Check points' },
    text: {
      fr: "Un point d’inspection est une étape de l'inspection (ex : face avant, carte grise…) pendant laquelle l'utilisateur prend une photo ou filme une vidéo dans l'application mobile. Les points d’inspection sont ensuite assemblés dans les types d'inspection.",
      en: 'A check point is a step of the inspection (e.g. front side, registration certificate…) during which the user takes a photo or records a video in the mobile app. Check points are then assembled in inspection types.',
    },
    docSlug: 'points-d-inspection',
  },
  checkPointTypeColumn: {
    title: { fr: 'Type de point d’inspection', en: 'Check point type' },
    text: {
      fr: "Photo : l'utilisateur prend une photo. Vidéo : l'utilisateur filme pendant une durée paramétrée, puis l'application passe au point d’inspection suivant. Un type d'inspection a une liste de points photo et une liste de points vidéo.",
      en: 'Photo: the user takes a picture. Video: the user records for a configured duration, then the app moves to the next check point. An inspection type has a list of photo check points and a list of video check points.',
    },
    docSlug: 'points-d-inspection',
  },
  checkPointIconsColumn: {
    title: { fr: 'Icônes et masque', en: 'Icons and mask' },
    text: {
      fr: "Icône rapport : affichée dans le rapport d'inspection. Icône app : affichée dans l'application mobile. Masque : visuel utilisé par l'application mobile pour guider le cadrage de ce point d’inspection.",
      en: 'Report icon: displayed in the inspection report. App icon: displayed in the mobile app. Mask: visual used by the mobile app to guide the framing of this check point.',
    },
    docSlug: 'points-d-inspection',
  },

  /* === Equipments === */
  equipmentsTable: {
    title: { fr: 'Les équipements', en: 'Equipments' },
    text: {
      fr: "Les équipements sont les éléments du véhicule que l'IA sait détecter (portière, rétroviseur, roue…). Sur chaque point d’inspection, on choisit les équipements obligatoires et facultatifs à détecter pour valider l'étape.",
      en: 'Equipments are the vehicle parts the AI can detect (door, mirror, wheel…). On each check point, you choose the required and optional equipments to detect to validate the step.',
    },
    docSlug: 'equipements',
  },
  equipmentMultipleColumn: {
    title: { fr: 'Équipement multiple', en: 'Multiple equipment' },
    text: {
      fr: "Coché lorsque l'équipement peut apparaître plusieurs fois sur un même point d’inspection (ex : les roues). Le nombre maximum d'occurrences se règle dans la fiche de l'équipement.",
      en: 'Ticked when the equipment can appear several times on the same check point (e.g. wheels). The maximum number of occurrences is set in the equipment form.',
    },
    docSlug: 'equipements',
  },

  /* === Damages === */
  damagesTable: {
    title: { fr: 'Les dommages', en: 'Damages' },
    text: {
      fr: "Référentiel des types de dommages (rayure, bosse…) utilisés dans les résultats d'inspection. Chaque dommage a un niveau de sévérité (1, 2 ou 3) : un même nom peut exister avec plusieurs niveaux.",
      en: 'List of the damage types (scratch, dent…) used in inspection results. Each damage has a severity level (1, 2 or 3): the same name can exist with several levels.',
    },
    docSlug: 'dommages',
  },

  /* === Inspection types === */
  inspectionTypesTable: {
    title: { fr: "Les types d'inspection", en: 'Inspection types' },
    text: {
      fr: "Un type d'inspection définit le parcours suivi dans l'application mobile : la liste ordonnée des points d’inspection photo et vidéo à réaliser. Il est choisi lors d'une demande d'inspection.",
      en: 'An inspection type defines the journey followed in the mobile app: the ordered list of photo and video check points to perform. It is chosen when requesting an inspection.',
    },
    docSlug: 'types-d-inspection',
  },
  inspectionTypeGroupColumn: {
    title: { fr: 'Groupe', en: 'Group' },
    text: {
      fr: "Groupe auquel ce type d'inspection est rattaché. « -- » lorsqu'aucun groupe n'est renseigné.",
      en: 'Group this inspection type is linked to. "--" when no group is set.',
    },
    docSlug: 'types-d-inspection',
  },
  inspectionTypePerformedByColumn: {
    title: { fr: 'Réalisée par', en: 'Performed by' },
    text: {
      fr: "Publics pour lesquels ce type d'inspection est disponible : professionnel, particulier ou les deux.",
      en: 'Audiences for which this inspection type is available: professional, individual or both.',
    },
    docSlug: 'types-d-inspection',
  },
  inspectionTypeCheckPointsBlock: {
    title: {
      fr: 'Points d’inspection du parcours',
      en: 'Check points of the journey',
    },
    text: {
      fr: "Ajoutez les points d’inspection à réaliser et glissez-déposez-les pour changer leur ordre de passage. Pour chacun, vous pouvez afficher une flèche (avec sa direction) et un texte d'information dans l'application. Chaque modification est enregistrée immédiatement ; retirer un point d’inspection ne le supprime pas, il pourra être ajouté à nouveau.",
      en: 'Add the check points to perform and drag and drop them to change their order. For each one, you can display an arrow (with its direction) and an information text in the app. Each change is saved immediately; removing a check point does not delete it, it can be added again.',
    },
    docSlug: 'types-d-inspection',
  },

  /* === Email templates === */
  emailTemplatesTable: {
    title: { fr: "Les templates d'email", en: 'Email templates' },
    text: {
      fr: "Chaque template fait le lien avec un template existant sur Brevo (via son ID). Le contenu de l'email se modifie sur Brevo ; ici, on lui donne un nom, un type et un statut.",
      en: 'Each template links to a template existing in Brevo (through its ID). The email content is edited in Brevo; here you give it a name, a type and a status.',
    },
    docSlug: 'templates-email',
  },
  emailTemplateTypeColumn: {
    title: { fr: 'Type de template', en: 'Template type' },
    text: {
      fr: "Situation dans laquelle le template est utilisé : création de compte, mot de passe oublié, demande d'inspection ou autre. Les emails automatisés utilisent les templates de type « Demande d'inspection ».",
      en: 'Situation in which the template is used: account creation, forgot password, inspection request or other. Automated emails use templates of type "Inspection request".',
    },
    docSlug: 'templates-email',
  },

  /* === Email automation === */
  emailGeneralSettingsBlock: {
    title: { fr: 'Configuration générale', en: 'General settings' },
    text: {
      fr: "Active ou coupe l'envoi de tous les emails automatisés, et renseigne le token API Brevo utilisé pour les envoyer. Chaque enregistrement est confirmé avant d'être appliqué.",
      en: 'Turns on or off the sending of all automated emails, and sets the Brevo API token used to send them. Each save is confirmed before being applied.',
    },
    docSlug: 'automatisation-des-emails',
  },
  emailVariablesBlock: {
    title: { fr: 'Variables', en: 'Variables' },
    text: {
      fr: "Note libre listant les variables envoyées en paramètres aux templates Brevo. Elle sert d'aide-mémoire pour rédiger les templates et n'a pas d'effet sur l'envoi.",
      en: 'Free note listing the variables sent as parameters to Brevo templates. It is a reminder to write the templates and has no effect on sending.',
    },
    docSlug: 'automatisation-des-emails',
  },
} satisfies Record<string, HelpTip>;
